import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const RewardWinners = () => {
  const navigate = useNavigate();
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;
  const [winners, setWinners] = useState([]);
  const [search, setSearch] = useState('');
  const [month, setMonth] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const recordsPerPage = 10;

  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({ id: '', prizeImage: '' });

  useEffect(() => {
    fetchWinners();
  }, [currentPage, search, month]);

  const fetchWinners = async () => {
    try {
      const response = await apiService.post('winnersList', {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: search,
        month: month
      });

      if (response.data) {
        setWinners(response.data);
        setTotalRecords(response.recordsFiltered || response.data.length);
        setTotalPages(Math.ceil((response.recordsFiltered || response.data.length) / recordsPerPage));
      }
    } catch (error) {
      console.error('Error fetching winners:', error);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ value: monthStr, label: monthName });
    }
    return options;
  };

  const editWinner = (id, currentImage) => {
    setEditData({ id, prizeImage: currentImage || '' });
    setShowEditModal(true);
  };

  const handleUpdateWinner = async (e) => {
    e.preventDefault();
    try {
      const response = await apiService.put(`updateWinner/${editData.id}`, {
        prizeImage: editData.prizeImage
      });

      if (response.success) {
        toast.success('Winner updated successfully');
        setShowEditModal(false);
        fetchWinners();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong!');
      console.error(error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata'
    });
  };

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="bg-white rounded-lg shadow-sm m-6">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <nav className="flex text-sm text-gray-600 mb-2">
              <span className="cursor-pointer hover:text-[#009ef7]" onClick={() => navigate('/dashboard')}>Home</span>
              <span className="mx-2">/</span>
              <span className="text-gray-900">Reward Winners</span>
            </nav>
          </div>
        </div>

        <div className="p-6">
          <div className="flex gap-4 mb-6">
            <div className="w-48">
              <input
                type="month"
                value={month}
                onChange={(e) => { setMonth(e.target.value); setCurrentPage(1); }}
                className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              />
            </div>
            <div className="flex-1">
              <div className="relative">
                <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  placeholder="Search winners..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-sm">
                  <th className="px-4 py-3 text-left font-semibold">#</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[150px]">Winner Name</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[150px]">Email</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[100px]">Phone</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[100px]">Month</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[100px]">Prize Image</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[100px]">Won Date</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[100px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {winners.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                      No winners found
                    </td>
                  </tr>
                ) : (
                  winners.map((winner, index) => (
                    <tr key={winner._id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                      <td className="px-4 py-3 text-gray-900">
                        {winner.userId ? `${winner.userId.first_name} ${winner.userId.last_name}` : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {winner.userId?.email || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {winner.userId?.phone || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-gray-900">{winner.month || '--'}</td>
                      <td className="px-4 py-3">
                        {winner.prizeImage ? (
                          <img
                            src={`${mediaUrl}${winner.prizeImage}`}
                            alt="Prize"
                            className="w-12 h-12 object-cover rounded"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="%23999" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"%3E%3C/rect%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"%3E%3C/circle%3E%3Cpolyline points="21 15 16 10 5 21"%3E%3C/polyline%3E%3C/svg%3E';
                            }}
                          />
                        ) : (
                          <span className="text-gray-500">No Image</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-900 text-sm">
                        {formatDate(winner.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => editWinner(winner._id, winner.prizeImage)}
                          className="px-3 py-1 text-xs font-semibold rounded"
                          style={{ backgroundColor: '#e8f4fd', color: '#009ef7' }}
                        >
                          <i className="bi bi-pencil text-sm"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalRecords > 0 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-600">
                Showing {(currentPage - 1) * recordsPerPage + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 text-sm border rounded ${
                      currentPage === page
                        ? 'text-white'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    style={currentPage === page ? { backgroundColor: '#009ef7', borderColor: '#009ef7' } : {}}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Edit Winner</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleUpdateWinner}>
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prize Image Filename
                  </label>
                  <input
                    type="text"
                    value={editData.prizeImage}
                    onChange={(e) => setEditData({ ...editData, prizeImage: e.target.value })}
                    placeholder="Enter prize image filename"
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
                  style={{ backgroundColor: '#009ef7' }}
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardWinners;
